import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { Field, Text, Textarea, Toggle } from "@/admin/components/AdminField";
import ImageUpload, { GalleryUpload } from "@/admin/components/ImageUpload";
import RichTextEditor from "@/admin/components/RichTextEditor";

const EMPTY = {
  slug: "", title: "", category: "Geral", excerpt: "", content: "",
  cover_image: "", gallery: [],
  author: "Equipe ROTTA", date: new Date().toISOString().slice(0, 10),
  read_time: "5 min", published: true,
  seo: { meta_title: "", meta_description: "", og_image: "" },
};

export default function BlogForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      adminApi.get(`/admin/blog/${id}`).then((r) => setF({ ...EMPTY, ...r.data, seo: { ...EMPTY.seo, ...(r.data.seo || {}) } }))
        .catch(() => toast.error("Falha ao carregar")).finally(() => setLoading(false));
    }
  }, [id, editing]);

  const set = (patch) => setF((s) => ({ ...s, ...patch }));
  const setSeo = (patch) => setF((s) => ({ ...s, seo: { ...s.seo, ...patch } }));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) await adminApi.put(`/admin/blog/${id}`, f);
      else await adminApi.post(`/admin/blog`, f);
      toast.success(editing ? "Artigo atualizado" : "Artigo criado");
      navigate("/admin/blog");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao salvar");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-[#B8B8B8]"><Loader2 className="inline animate-spin" size={16} /> Carregando…</div>;

  return (
    <form onSubmit={submit} className="space-y-8 max-w-5xl" data-testid="blog-form">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <Link to="/admin/blog" className="text-[10px] tracking-[0.25em] uppercase text-[#B8B8B8] inline-flex items-center gap-2 hover:text-[#C6A762]">
            <ArrowLeft size={12} /> Voltar
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl mt-3">{editing ? "Editar artigo" : "Novo artigo"}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <Toggle value={f.published} onChange={(v) => set({ published: v })} label="Publicado" />
          <button disabled={saving} className="btn-gold px-6 py-3 rounded-full text-xs inline-flex items-center gap-2 ml-auto sm:ml-0">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
          </button>
        </div>
      </div>

      <section className="card-rotta p-4 sm:p-7 bg-[#141414] space-y-5">
        <Field label="Título"><Text value={f.title} onChange={(v) => set({ title: v, slug: f.slug || v })} required /></Field>
        <div className="grid md:grid-cols-3 gap-5">
          <Field label="Slug"><Text value={f.slug} onChange={(v) => set({ slug: v })} /></Field>
          <Field label="Categoria"><Text value={f.category} onChange={(v) => set({ category: v })} placeholder="Dicas de Viagem" /></Field>
          <Field label="Autor"><Text value={f.author} onChange={(v) => set({ author: v })} /></Field>
          <Field label="Data"><Text type="date" value={f.date} onChange={(v) => set({ date: v })} required /></Field>
          <Field label="Tempo de leitura"><Text value={f.read_time} onChange={(v) => set({ read_time: v })} placeholder="5 min" /></Field>
        </div>
        <Field label="Resumo (excerpt)"><Textarea rows={2} value={f.excerpt} onChange={(v) => set({ excerpt: v })} /></Field>
      </section>

      <section className="card-rotta p-4 sm:p-7 bg-[#141414] space-y-5">
        <ImageUpload label="Imagem de capa" value={f.cover_image} onChange={(v) => set({ cover_image: v })} folder="rotta/blog" />
        <GalleryUpload label="Galeria" value={f.gallery} onChange={(v) => set({ gallery: v })} folder="rotta/gallery" />
      </section>

      <section className="card-rotta p-4 sm:p-7 bg-[#141414] space-y-3">
        <div className="text-[11px] tracking-[0.2em] uppercase text-[#C6A762]">Conteúdo</div>
        <RichTextEditor value={f.content} onChange={(v) => set({ content: v })} placeholder="Escreva o artigo…" />
      </section>

      <section className="card-rotta p-4 sm:p-7 bg-[#141414] space-y-5">
        <h2 className="font-display tracking-wider uppercase text-sm text-[#C6A762]">SEO</h2>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Meta Title"><Text value={f.seo.meta_title} onChange={(v) => setSeo({ meta_title: v })} /></Field>
          <Field label="OG Image URL"><Text value={f.seo.og_image} onChange={(v) => setSeo({ og_image: v })} /></Field>
          <Field label="Meta Description" cols={2}><Textarea rows={2} value={f.seo.meta_description} onChange={(v) => setSeo({ meta_description: v })} /></Field>
        </div>
      </section>

      <div className="flex justify-end gap-3">
        <Link to="/admin/blog" className="btn-ghost px-6 py-3 rounded-full text-xs">Cancelar</Link>
        <button disabled={saving} className="btn-gold px-6 py-3 rounded-full text-xs inline-flex items-center gap-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
        </button>
      </div>
    </form>
  );
}
